const AUTH_STORAGE_KEY = "deuxda_admin_auth";
const TOKEN_STORAGE_KEY = "deuxda_admin_token";

/** Deuxda API origin. Local dev uses the Deuxda API on port 4000; the deployed admin uses api.deuxda.com. */
export function getAPIBaseURL() {
  if (typeof window === 'undefined') return "https://api.deuxda.com";
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  return isLocalhost ? "http://localhost:4000" : "https://api.deuxda.com";
}

export interface AdminUser {
  id: string;
  email: string;
  role: "Super Admin" | "Admin" | "Operations" | "Support" | "Finance" | "Moderator";
  name: string;
}

export function setAuthUser(user: AdminUser) {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
}

export function getAuthUser(): AdminUser | null {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  return stored ? JSON.parse(stored) : null;
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setAuthToken(token: string) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearAuthUser() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function isAuthenticated(): boolean {
  return getAuthUser() !== null && getAuthToken() !== null;
}

export async function loginAdmin(email: string, password: string): Promise<AdminUser> {
  const apiBase = getAPIBaseURL();

  // Same login as the mobile app; admin rights are enforced by the server, not by this screen
  const response = await fetch(`${apiBase}/api/v1/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Connexion impossible (${response.status})`);
  }

  const result = await response.json();
  const token: string = result.token;
  const userData = result.user;

  // A non-admin account is refused with 403 by every admin route
  const probe = await fetch(`${apiBase}/api/v1/admin/refunds?status=pending_review`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (probe.status === 403) throw new Error("Ce compte n'est pas administrateur");
  if (!probe.ok) throw new Error(`Connexion impossible (${probe.status})`);

  const user: AdminUser = {
    id: userData.id,
    email: userData.email,
    name: `${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim(),
    role: "Super Admin",
  };

  setAuthToken(token);
  setAuthUser(user);
  return user;
}

export function logout() {
  clearAuthUser();
}

export function getAuthHeaders() {
  const token = getAuthToken();
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export function getAdminHeaders() {
  return getAuthHeaders();
}

let unauthorizedHandlerInstalled = false;

/**
 * When the admin token expires, every API call returns 401. Send the user to the
 * login page instead of leaving the screens empty. Login itself is excluded so a
 * wrong password still shows its own error.
 */
export function installUnauthorizedHandler() {
  if (unauthorizedHandlerInstalled || typeof window === "undefined") return;
  unauthorizedHandlerInstalled = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input, init);
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const isAdminApi = url.startsWith(getAPIBaseURL());
    const isLogin = url.includes("/auth/login");
    if (response.status === 401 && isAdminApi && !isLogin && window.location.pathname !== "/login") {
      clearAuthUser();
      window.location.replace("/login");
    }
    return response;
  };
}
